/**
 * Detour route management: fetch, cache, and draw start→POI→end routes.
 *
 * Renders the full detour route as a black dashed line.
 * The main route (solid green) covers any shared parts;
 * only the actual deviation from the main route is visible.
 */

import L from "leaflet";
import type { LatLng } from "./types";
import type { ParsedPoi } from "./poi";

// ─── Cache ────────────────────────────────────────────────────────────────────

interface CachedRoute {
  geometry: LatLng[];
  durationS: number;
  distanceM: number;
}

const routeCache = new Map<string, CachedRoute>();

function cacheKey(poi: ParsedPoi, start: LatLng, end: LatLng): string {
  // 5 decimal places ≈ 1.1 m precision — good enough for routing
  return (
    start.lat.toFixed(5) + "," + start.lng.toFixed(5) + "|" +
    poi.lat.toFixed(5) + "," + poi.lng.toFixed(5) + "|" +
    end.lat.toFixed(5) + "," + end.lng.toFixed(5)
  );
}

// ─── State ────────────────────────────────────────────────────────────────────

let activeDetourLayer: L.LayerGroup | null = null;
let selectedPoiId: string | null = null;

export function getSelectedPoiId(): string | null {
  return selectedPoiId;
}

// ─── Route fetching ───────────────────────────────────────────────────────────

function decodePolyline(encoded: string): LatLng[] {
  const coords: LatLng[] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, byte: number;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; }
    while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; }
    while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : result >> 1;
    coords.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return coords;
}

export async function fetchDetourRoute(
  poi:   ParsedPoi,
  start: LatLng,
  end:   LatLng,
): Promise<CachedRoute> {
  const key = cacheKey(poi, start, end);
  if (routeCache.has(key)) return routeCache.get(key)!;

  const url =
    "https://router.project-osrm.org/route/v1/driving/" +
    `${start.lng},${start.lat};${poi.lng},${poi.lat};${end.lng},${end.lat}` +
    "?overview=full&geometries=polyline";

  const res = await fetch(url);
  if (!res.ok) throw new Error(`OSRM error: ${res.status}`);
  const data = await res.json();
  if (data.code !== "Ok" || !data.routes?.length) {
    throw new Error(`No route found (${data.code})`);
  }

  const r   = data.routes[0];
  const route: CachedRoute = {
    geometry:   decodePolyline(r.geometry),
    durationS:  r.duration,
    distanceM:  r.distance,
  };
  routeCache.set(key, route);
  return route;
}

// ─── Shared-path detection ─────────────────────────────────────────────────────

/**
 * Find the index of the last point in the shared prefix of two polylines
 * and the index of the first point in the shared suffix, using a snapped point.
 *
 * @param orig     Original route geometry (start → end)
 * @param detour   Detour route geometry (start → POI → end)
 * @param snapM    Snap radius in metres
 */
function findSharedBounds(
  orig:   LatLng[],
  detour: LatLng[],
  snapM:  number,
): { shareEnd: number; shareStart: number } {
  // ── Shared prefix (start → first divergence) ──────────────────────────────
  let shareEnd = 0;
  outer: for (let i = 0; i < orig.length; i++) {
    for (let j = 0; j < detour.length; j++) {
      if (haversineM(orig[i], detour[j]) <= snapM) {
        shareEnd = i;
        break outer;
      }
    }
  }

  // ── Shared suffix (last convergence → end) — search backwards ──────────────
  let shareStart = orig.length - 1;
  outer: for (let i = orig.length - 1; i >= shareEnd; i--) {
    for (let j = detour.length - 1; j >= 0; j--) {
      if (haversineM(orig[i], detour[j]) <= snapM) {
        shareStart = i;
        break outer;
      }
    }
  }

  return { shareEnd, shareStart };
}

function haversineM(a: LatLng, b: LatLng): number {
  const R   = 6.371e6;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// ─── Drawing ─────────────────────────────────────────────────────────────────

const SEGMENT_COLOR = "#000000"; // black — only the divergent part, dotted

export function drawDetourRoute(
  poi:        ParsedPoi,
  start:      LatLng,
  end:        LatLng,
  map:        L.Map,
  origGeom:   LatLng[],
): { layerGroup: L.LayerGroup } {
  // Remove previous detour layers
  clearDetourRoute();

  selectedPoiId = poi.id;
  activeDetourLayer = L.layerGroup().addTo(map);

  const route = routeCache.get(cacheKey(poi, start, end));
  if (!route) return { layerGroup: activeDetourLayer };

  // ── Full detour route (black dashed) ───────────────────────────────────────
  // Any overlap with the main route is hidden under the solid green line.
  L.polyline(
    route.geometry.map(p => [p.lat, p.lng] as L.LatLngTuple),
    { color: SEGMENT_COLOR, weight: 4, opacity: 0.8, interactive: false,
      dashArray: "8, 8" },
  ).addTo(activeDetourLayer);

  return { layerGroup: activeDetourLayer };
}

export function clearDetourRoute(): void {
  if (activeDetourLayer) {
    activeDetourLayer.remove();
    activeDetourLayer = null;
  }
  selectedPoiId = null;
}
